import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Add token to requests
    api.interceptors.request.use((config) => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
    });

    // Handle 401 errors
    api.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401 && !error.config._retry) {
                // Mark as retry to prevent infinite loop
                error.config._retry = true;
                // Only logout if we were already authenticated
                const currentToken = localStorage.getItem('token');
                if (currentToken) {
                    console.log('Session expired, logging out...');
                    logout();
                }
            }
            return Promise.reject(error);
        }
    );

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        const response = await api.post('/auth/login', { email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const hasRole = (...roles) => {
        return user && roles.includes(user.role);
    };

    const isCommercial = () => hasRole('commercial');
    const isAssistant = () => hasRole('assistant');
    const isEditorial = () => hasRole('editorial');
    const isSuperAdmin = () => hasRole('super_admin');
    const isAdmin = () => hasRole('super_admin', 'assistant');

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                loading,
                login,
                logout,
                api,
                hasRole,
                isCommercial,
                isAssistant,
                isEditorial,
                isSuperAdmin,
                isAdmin,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
